import React, { useEffect, useRef, useState } from 'react';
import { UI, CSS_CLASSES } from '../constants';

interface YearInputProps {
  yearInput: string[];
  onDigitInput: (index: number, value: string) => void;
  onKeyDown: (index: number, e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  feedbackMessage?: string;
  hasGuessed?: boolean;
  onEnterPress?: () => void;
  hintRequested?: boolean;
  validationError?: string;
  hintMessage?: string;
  selectingDigit?: boolean;
  revealedDigits?: boolean[];
  challengeRevealedDigits?: boolean[];
  onDigitReveal?: (index: number) => void;
}

const YearInput: React.FC<YearInputProps> = ({
  yearInput,
  onDigitInput,
  onKeyDown,
  onPaste,
  disabled = false,
  feedbackMessage = '',
  hasGuessed = false,
  onEnterPress,
  hintRequested = false,
  validationError,
  hintMessage,
  selectingDigit = false,
  revealedDigits = [false, false, false, false],
  challengeRevealedDigits = [false, false, false, false],
  onDigitReveal,
}) => {
  // Refs for each input field
  const inputRefs = useRef<Array<HTMLInputElement | null>>([
    null,
    null,
    null,
    null,
  ]);

  // State to track the flipping animation
  const [flippingDigits, setFlippingDigits] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  // Track the previous revealed digits to detect changes
  const prevRevealedRef = useRef<boolean[]>([false, false, false, false]);

  // Track the previously focused input - used for restoring focus on new round
  const lastFocusedInput = useRef<number>(0);

  // Check if we're on a mobile device
  const isMobile = () => {
    return (
      window.innerWidth <= UI.MOBILE_BREAKPOINT ||
      navigator.maxTouchPoints > 0 ||
      /Mobi|Android/i.test(navigator.userAgent)
    );
  };

  // Function to select all text in input
  const selectAllText = (input: HTMLInputElement) => {
    if (input) {
      setTimeout(() => {
        input.select();
      }, 0);
    }
  };

  // Function to scroll to top on mobile screens
  const scrollToTopOnMobile = () => {
    if (isMobile()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Function to find the next available (non-disabled) input field
  const findNextAvailableField = (currentIndex: number) => {
    for (let i = currentIndex + 1; i < 4; i++) {
      if (!revealedDigits[i]) {
        return i;
      }
    }
    return -1; // No available field found
  };

  // Function to focus on the next available field
  const focusNextAvailableField = (currentIndex: number) => {
    const nextIndex = findNextAvailableField(currentIndex);
    if (nextIndex !== -1) {
      const nextInput = inputRefs.current[nextIndex];
      if (nextInput) {
        setTimeout(() => {
          nextInput.focus();
          selectAllText(nextInput);
        }, 0);
      }
    }
  };

  // Handle click on input to select all text and scroll on mobile
  const handleInputClick = (input: HTMLInputElement) => {
    scrollToTopOnMobile();
    selectAllText(input);
  };

  // Handle digit click for revealing
  const handleDigitClick = (index: number) => {
    if (selectingDigit && onDigitReveal && !challengeRevealedDigits[index]) {
      onDigitReveal(index);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Handle Enter key
    if (e.key === 'Enter' && onEnterPress) {
      onEnterPress();
      return;
    }

    // Handle up and down arrow keys to increment/decrement values
    // But do NOT advance to the next field
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentValue = yearInput[index]
        ? parseInt(yearInput[index], 10)
        : -1;
      const newValue = (currentValue + 1) % 10; // Cycle from 0-9
      onDigitInput(index, newValue.toString());
      // Stay in the same field - no auto-advance
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentValue = yearInput[index]
        ? parseInt(yearInput[index], 10)
        : 0;
      const newValue = (currentValue - 1 + 10) % 10; // Cycle from 9-0
      onDigitInput(index, newValue.toString());
      // Stay in the same field - no auto-advance
    }
    // Handle moving cursor between inputs
    else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      // Find the previous non-disabled field
      let prevIndex = index - 1;
      while (prevIndex >= 0 && revealedDigits[prevIndex]) {
        prevIndex--;
      }

      if (prevIndex >= 0) {
        const prevInput = inputRefs.current[prevIndex];
        if (prevInput) {
          prevInput.focus();
          selectAllText(prevInput);
        }
      }
    } else if (e.key === 'ArrowRight' && index < 3) {
      e.preventDefault();
      // Find the next non-disabled field
      focusNextAvailableField(index);
    } else if (e.key === 'Backspace') {
      // If there's a value in current field, delete it but STAY in the current field
      if (yearInput[index]) {
        e.preventDefault();
        onDigitInput(index, '');
        // Don't move to previous field after deletion - stay in current field
      }
      // If current field is empty, THEN move to previous field
      else if (index > 0) {
        e.preventDefault();
        // Find the previous non-disabled field
        let prevIndex = index - 1;
        while (prevIndex >= 0 && revealedDigits[prevIndex]) {
          prevIndex--;
        }

        if (prevIndex >= 0) {
          const prevInput = inputRefs.current[prevIndex];
          if (prevInput) {
            prevInput.focus();
            selectAllText(prevInput);
          }
        }
      }
    }

    // Call the original handler for other keys
    onKeyDown(index, e);
  };

  // Set focus on first input field on component mount (for desktop only)
  useEffect(() => {
    if (!isMobile() && !disabled && !selectingDigit) {
      // Find the first non-revealed input to focus
      let firstAvailableIndex = -1;
      for (let i = 0; i < 4; i++) {
        if (!revealedDigits[i]) {
          firstAvailableIndex = i;
          break;
        }
      }

      // Focus on the first available input field
      if (firstAvailableIndex !== -1) {
        const input = inputRefs.current[firstAvailableIndex];
        if (input) {
          // Use a small delay to ensure the DOM is ready
          setTimeout(() => {
            input.focus();
            selectAllText(input);
          }, 50);
        }
      }
    }
  }, [disabled, selectingDigit, revealedDigits]); // Added dependencies to retrigger on new rounds

  useEffect(() => {
    const preventScroll = (e: Event) => {
      if (document.activeElement instanceof HTMLInputElement) {
        // Check if the active element is one of our year inputs
        if (
          document.activeElement.id.startsWith(CSS_CLASSES.YEAR_INPUT_PREFIX)
        ) {
          window.scrollTo(0, 0);
        }
      }
    };

    window.addEventListener('resize', preventScroll);

    return () => {
      window.removeEventListener('resize', preventScroll);
    };
  }, []);

  // Effect to handle the flipping animation and clearing non-revealed digits
  useEffect(() => {
    // Compare current and previous revealed digits to detect changes
    let hasNewReveal = false;
    const newFlippingDigits = [...flippingDigits];

    revealedDigits.forEach((revealed, index) => {
      if (revealed && !prevRevealedRef.current[index]) {
        hasNewReveal = true;
        newFlippingDigits[index] = true;

        // Schedule clearing of the flipping flag
        setTimeout(() => {
          setFlippingDigits((prev) => {
            const updated = [...prev];
            updated[index] = false;
            return updated;
          });
        }, 500);
      }
    });

    // Update flipping digits if needed
    if (hasNewReveal) {
      setFlippingDigits(newFlippingDigits);

      // Clear ALL input fields except revealed ones
      // This guarantees that any time a digit is revealed, all non-revealed fields are cleared
      yearInput.forEach((digit, index) => {
        if (!revealedDigits[index]) {
          onDigitInput(index, '');
        }
      });

      // Remove focus from any active element and prevent refocusing
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Disable all input fields temporarily to prevent focus after reveal
      inputRefs.current.forEach((input) => {
        if (input) {
          input.setAttribute('tabindex', '-1');
          // Prevent iOS from focusing again by making inputs non-interactive
          input.style.pointerEvents = 'none';
          // Re-enable after animation completes
          setTimeout(() => {
            if (input) {
              input.removeAttribute('tabindex');
              input.style.pointerEvents = '';
            }
          }, 600);
        }
      });
    }

    // Update the previous revealed digits reference
    prevRevealedRef.current = [...revealedDigits];
  }, [revealedDigits, yearInput, onDigitInput, flippingDigits]);

  // Effect to prevent focus on mobile when first loading
  useEffect(() => {
    // If on mobile, immediately blur any focused element
    if (isMobile()) {
      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 100);
    }
  }, []);

  // Don't render the component if the user has already guessed
  if (hasGuessed) {
    return null;
  }

  return (
    <div>
      <div className="mb-3">
        {/*  {selectingDigit ? (
          <p className="text-white/70 text-[12px] uppercase font-bold">Select a digit to reveal</p>
        ) : null} */}
      </div>

      <div className="flex justify-center mb-3">
        <div className="flex">
          {yearInput.map((digit, index) => {
            // Determine if this digit is revealed in current round
            const isRevealed = revealedDigits[index];
            // Determine if this digit position was ever revealed in the challenge
            const isRevealedInChallenge = challengeRevealedDigits[index];
            // Determine if this digit is currently flipping
            const isFlipping = flippingDigits[index];

            // If we're in digit selection mode
            if (selectingDigit) {
              return (
                <div
                  key={index}
                  onClick={() => handleDigitClick(index)}
                  className={`w-[49px] h-16 flex items-center justify-center rounded mb-2 text-2xl ${index < 3 ? 'mr-3' : ''}
                    ${
                      isRevealedInChallenge
                        ? 'bg-[#444444] text-white/50 cursor-not-allowed'
                        : 'bg-[#FFFFFF] text-black cursor-pointer hover:bg-[#EEEEEE] transition-colors shake-animation'
                    }`}
                >
                  {/* Display nothing in cards during selection */}
                </div>
              );
            }

            // If this digit has been revealed in current round
            if (isRevealed) {
              return (
                <div
                  key={index}
                  className={`w-[49px] h-16 flex items-center font-bold justify-center rounded text-2xl ${index < 3 ? 'mr-3' : ''}
                    border border-[#333333]
                    ${isFlipping ? 'flip-card' : 'bg-[#444444]/50 text-white/70'}`}
                >
                  {isFlipping ? '' : digit}
                </div>
              );
            }

            // Regular input field when not revealed and not in selection mode
            return (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                id={`${CSS_CLASSES.YEAR_INPUT_PREFIX}${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => {
                  const value = e.target.value;

                  // Update the digit value
                  onDigitInput(index, value);

                  // ALWAYS auto-advance to next field if a digit was entered,
                  // regardless of whether we're replacing or entering new
                  if (value && value.length === 1 && index < 3) {
                    // Track the current input index before moving
                    lastFocusedInput.current = index + 1;

                    // Use multiple timeouts with increasing delays to ensure focus advancement works
                    // This creates a more robust solution for various browser timing issues

                    // First attempt - immediate
                    focusNextAvailableField(index);

                    // Second attempt - short delay
                    setTimeout(() => {
                      focusNextAvailableField(index);
                    }, 10);

                    // Third attempt - longer delay as backup
                    setTimeout(() => {
                      focusNextAvailableField(index);
                    }, 50);
                  }
                }}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onClick={(e) => handleInputClick(e.currentTarget)}
                onFocus={(e) => {
                  lastFocusedInput.current = index;
                  selectAllText(e.currentTarget);
                }}
                onPaste={index === 0 ? onPaste : undefined}
                className={`w-[49px] h-16 border rounded text-center text-2xl digit-input mb-2 font-bold ${index < 3 ? 'mr-3' : ''}
                  ${
                    disabled
                      ? 'bg-[#333333]/30 border-[#222222] text-white/50 cursor-not-allowed'
                      : 'bg-[#333333]/30 border-[#333333] text-white focus:outline-none focus:ring-1 focus:ring-[#7236DC] focus:border-[#7236DC]'
                  }`}
                autoComplete="off"
                autoFocus={
                  !isMobile() && index === 0 && !disabled && !selectingDigit
                }
                disabled={
                  disabled || (selectingDigit && !isRevealedInChallenge)
                }
              />
            );
          })}
        </div>
      </div>

      {/* Validation error */}
      {validationError && (
        <p className="text-red-400 text-xs mt-1 mb-3">{validationError}</p>
      )}
    </div>
  );
};

export default React.memo(YearInput);
